import { useCallback, useMemo } from 'react';

import { chartThemeTokens } from 'design-system';

import { DateTextVariants, useDateText } from '@/features/_shared/user';
import getRegularTooltip from '@/features/charts/hooks/tooltip/getRegularTooltip';
import useSeasonalTooltipFormatter from '@/features/charts/hooks/tooltip/useSeasonalTooltipFormatter';
import usePrefersReducedMotion from '@/features/charts/hooks/usePrefersReducedMotion';
import { withSeasonalSeriesAppearance } from '@/features/charts/lib/applyChartSeriesAppearance';
import { getDayMonthOfYear } from '@/features/charts/lib/getDayMonthOfYear';
import { createChartAppearance } from '@/features/charts/theme/chartAppearance';
import type {
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/features/charts/types/highcharts';

import { useForwardCurveOptionsWithAppearance } from './useForwardCurveOptions';

import type { XAxisOptions } from 'highcharts';

const X_AXIS_TICK_INTERVAL = 31;
const X_AXIS_MIN = -5;

/**
 * Configures X-axis options for seasonal charts.
 * Sets tick interval, min value, and month-based label formatting.
 */
const configureSeasonalXAxis = (
  xAxisOptions: XAxisOptions,
  formatDateString: (date: string, variant: DateTextVariants) => string
): void => {
  xAxisOptions.tickInterval = X_AXIS_TICK_INTERVAL;
  xAxisOptions.min = X_AXIS_MIN;

  if (xAxisOptions.labels) {
    xAxisOptions.labels.formatter = function (): string {
      const value = Number(this.value);
      return formatDateString(
        getDayMonthOfYear({ dayOfYear: value + 1 }).toJSON(),
        DateTextVariants.MONTH
      );
    };
  }
};

const useSeasonalOptions = (height?: number) => {
  const mode = 'regular' as const;
  const appearance = useMemo(() => createChartAppearance(chartThemeTokens, mode), [mode]);
  const prefersReducedMotion = usePrefersReducedMotion();
  const { createOptions: createFvCurveOptions } = useForwardCurveOptionsWithAppearance(
    height,
    true,
    appearance,
    prefersReducedMotion
  );
  const { createTooltipFormatter } = useSeasonalTooltipFormatter(appearance);
  const { formatDateString } = useDateText();

  const tooltip = useMemo(
    () => getRegularTooltip(createTooltipFormatter, appearance, prefersReducedMotion).tooltip,
    [appearance, createTooltipFormatter, prefersReducedMotion]
  );

  const createOptions = useCallback(
    (chartSeries: ExtendedSeriesOptionsType[], hiddenSeriesIds: string[] = []): ExtendedOptions => {
      const coloredSeries = chartSeries.map(series =>
        withSeasonalSeriesAppearance(series, appearance)
      );

      const baseOptions = createFvCurveOptions(coloredSeries, hiddenSeriesIds);
      const xAxisOptions = baseOptions.xAxis as XAxisOptions | undefined;

      if (xAxisOptions) {
        configureSeasonalXAxis(xAxisOptions, formatDateString);
      }

      return {
        ...baseOptions,
        xAxis: xAxisOptions,
        tooltip
      };
    },
    [appearance, createFvCurveOptions, formatDateString, tooltip]
  );

  return { createOptions };
};

export default useSeasonalOptions;
